import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { EmptyState } from '@/components/EmptyState';
import { timeAgo } from '@/lib/format';
import {
  History as HistoryIcon,
  BookmarkPlus,
  Bookmark,
  CalendarClock,
  Trash2,
  Play,
  Pause,
  PlayCircle,
  Edit3,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  Clock,
  ArrowLeft,
  Plus,
} from 'lucide-react';

/**
 * Scans hub — unified History / Saved Searches / Scheduled Scans.
 *
 * Doctrine v2.0:
 *   • History  — automatic. Unsaved scans purge after 90 days; saved forever.
 *   • Saved    — intentional. Named. Permanent.
 *   • Scheduled — background APScheduler + delta detection.
 */

const TABS = [
  { key: 'history', label: 'History', icon: HistoryIcon },
  { key: 'saved', label: 'Saved Searches', icon: Bookmark },
  { key: 'scheduled', label: 'Scheduled Scans', icon: CalendarClock },
];

const CADENCE_OPTIONS = [
  { value: 'daily', label: 'Daily', runTimes: 1 },
  { value: 'twice_daily', label: 'Twice a day', runTimes: 2 },
  { value: 'thrice_daily', label: 'Three times a day', runTimes: 3 },
  { value: 'weekly', label: 'Weekly', runTimes: 1 },
];

const WEEKDAYS = [
  { value: 'mon', label: 'Monday' }, { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' }, { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' }, { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

const Scans = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('history');

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6 space-y-6">
      <header>
        <h1 className="font-serif text-4xl text-zita-espresso">Scans</h1>
        <p className="mt-2 text-zita-mocha text-sm">
          Every scan is automatically kept in History for 90 days. Save the ones that matter,
          or schedule Zita to re-run a search on its own.
        </p>
      </header>

      <div className="flex items-center gap-1 border-b border-[color:var(--zita-hairline)] overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            data-testid={`scans-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-4 py-3 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition ${
              tab === key
                ? 'border-[color:var(--zita-blue)] zita-blue-text'
                : 'border-transparent zita-ink-muted hover:zita-ink-soft'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === 'history' && <HistoryTab user={user} />}
      {tab === 'saved' && <SavedTab user={user} />}
      {tab === 'scheduled' && <ScheduledTab user={user} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HISTORY tab — every scan the user has run (90-day rolling window unless saved)
// ---------------------------------------------------------------------------

const HistoryTab = ({ user }) => {
  const [scans, setScans] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [retentionDays, setRetentionDays] = useState(90);
  // Aug 2026 §16 — Bulk history management.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { action, count } | null
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/scans/history', { params: { kind: 'all', q } });
      setScans(data.scans || []);
      setRetentionDays(data.retention_days || 90);
    } catch (e) {
      toast.error('Could not load history.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // A scan is "protected" from bulk clear when it's saved OR linked to an
  // active Loop Scan. Users can still act on protected rows via per-item
  // controls (Unsave / Stop Loop) but bulk clear can never sweep them.
  const isProtected = (s) =>
    !!s.is_saved || !!s.schedule_id ||
    (s.status === 'queued' || s.status === 'running');

  const eligible = scans.filter((s) => !isProtected(s));
  const eligibleIds = eligible.map((s) => s.id);
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(eligibleIds));
  const deselectAll = () => setSelected(new Set());
  const enterSelectMode = () => { setSelectMode(true); setSelected(new Set()); };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const savePermanent = async (scan) => {
    const name = window.prompt('Name this saved search:', scan.command_text || 'Saved search');
    if (!name) return;
    try {
      await api.post(`/scans/${scan.id}/save`, { name });
      toast.success(`Saved: ${name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); }
  };

  const doDelete = async (scan) => {
    if (!window.confirm('Delete this scan from history? This cannot be undone.')) return;
    try {
      await api.delete(`/scans/${scan.id}`);
      toast.success('Deleted.');
      load();
    } catch { toast.error('Could not delete.'); }
  };

  const rerun = async (scan) => {
    try {
      const { data } = await api.post(`/scans/${scan.id}/rerun`);
      toast.success('New scan queued.');
      window.location.assign(`/results/${data.scan_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rerun.'); }
  };

  const runBulk = async (action, allUnsaved) => {
    setBusy(true);
    try {
      const body = allUnsaved
        ? { action, all_unsaved: true }
        : { action, scan_ids: Array.from(selected) };
      const { data } = await api.post('/scans/history/bulk', body);
      const changed = data.cleared ?? data.archived ?? 0;
      const protectedCount = data.protected || 0;
      if (action === 'clear' || action === 'delete') {
        toast.success(`Cleared ${changed} scan${changed !== 1 ? 's' : ''}` +
          (protectedCount ? ` (${protectedCount} protected)` : ''));
      } else {
        toast.success(`Archived ${changed} scan${changed !== 1 ? 's' : ''}` +
          (protectedCount ? ` (${protectedCount} protected)` : ''));
      }
      exitSelectMode();
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action}.`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
          placeholder="Filter by search phrase…"
          data-testid="history-search-input"
          className="zita-input px-3 py-2 text-sm flex-1 min-w-[220px]"
        />
        <button type="button" onClick={load} data-testid="history-search-btn" className="zita-btn-secondary text-sm">
          Search
        </button>
        {!selectMode ? (
          <>
            <button
              type="button"
              onClick={enterSelectMode}
              data-testid="history-enter-select-mode"
              disabled={scans.length === 0}
              className="zita-btn-secondary text-sm disabled:opacity-50"
            >
              Select
            </button>
            <Link to="/seek" data-testid="history-new-scan" className="zita-btn-primary text-sm inline-flex items-center gap-1">
              <Plus size={14} /> New scan
            </Link>
          </>
        ) : (
          <button
            type="button"
            onClick={exitSelectMode}
            data-testid="history-exit-select-mode"
            className="zita-btn-secondary text-sm"
          >
            Cancel
          </button>
        )}
      </div>

      {selectMode && (
        <div
          data-testid="history-bulk-toolbar"
          className="flex flex-wrap items-center gap-2 rounded-xl border border-zita-taupe bg-zita-sand/40 p-3 sticky top-2 z-20"
        >
          <label className="inline-flex items-center gap-2 text-sm text-zita-espresso cursor-pointer">
            <input
              type="checkbox"
              data-testid="history-select-all"
              checked={allSelected}
              onChange={(e) => (e.target.checked ? selectAll() : deselectAll())}
              className="w-4 h-4"
            />
            {allSelected ? 'Deselect all' : 'Select all'}
          </label>
          <span data-testid="history-selected-count" className="text-xs text-zita-mocha">
            {selected.size} of {eligibleIds.length} eligible selected
            {(scans.length - eligible.length) > 0 && (
              <span className="ml-2 text-zita-muted">
                · {scans.length - eligible.length} protected (saved / active Loop / running)
              </span>
            )}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setConfirm({ action: 'archive', count: selected.size, allUnsaved: false })}
              disabled={!someSelected || busy}
              data-testid="history-archive-selected"
              className="zita-btn-secondary text-sm disabled:opacity-50"
            >
              Archive selected ({selected.size})
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ action: 'clear', count: selected.size, allUnsaved: false })}
              disabled={!someSelected || busy}
              data-testid="history-clear-selected"
              className="zita-btn-secondary text-sm text-red-700 disabled:opacity-50"
            >
              Clear selected ({selected.size})
            </button>
            <button
              type="button"
              onClick={() => setConfirm({ action: 'clear', count: eligibleIds.length, allUnsaved: true })}
              disabled={eligibleIds.length === 0 || busy}
              data-testid="history-clear-all"
              className="zita-btn-primary text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              Clear all ({eligibleIds.length})
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm zita-ink-muted">Loading…</div>
      ) : scans.length === 0 ? (
        <EmptyState icon={HistoryIcon} title="No scans yet" body="Head to Seek and run your first scan." />
      ) : (
        <div className="space-y-3" data-testid="history-scan-list">
          {scans.map((s) => {
            const prot = isProtected(s);
            const checked = selected.has(s.id);
            return (
              <div
                key={s.id}
                className={selectMode ? 'flex items-start gap-3' : ''}
                data-testid={`history-row-${s.id}`}
              >
                {selectMode && (
                  <div className="pt-4 shrink-0" data-testid={`history-checkbox-wrap-${s.id}`}>
                    <input
                      type="checkbox"
                      disabled={prot}
                      checked={checked}
                      onChange={() => toggleOne(s.id)}
                      data-testid={`history-checkbox-${s.id}`}
                      className="w-4 h-4 disabled:opacity-40"
                      aria-label={prot ? 'Protected — cannot bulk-clear' : 'Select scan'}
                      title={prot ? 'Protected: saved searches and active Loops cannot be bulk-cleared' : ''}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <ScanCard
                    scan={s} retentionDays={retentionDays}
                    onSave={savePermanent} onDelete={doDelete} onRerun={rerun}
                  />
                  {selectMode && prot && (
                    <div
                      data-testid={`history-protected-${s.id}`}
                      className="text-[11px] text-zita-muted mt-1 pl-1"
                    >
                      Protected — {s.is_saved ? 'saved search' : s.schedule_id ? 'active Loop Scan' : 'still running'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setConfirm(null)}
          data-testid="history-bulk-confirm-overlay"
        >
          <div
            className="zita-card p-6 max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="font-serif text-lg text-zita-espresso">
              {confirm.action === 'archive' ? 'Archive scans?' : 'Clear scans permanently?'}
            </div>
            <div className="text-sm text-zita-mocha leading-relaxed">
              {confirm.action === 'archive' ? (
                <>Move <b>{confirm.count}</b> scan{confirm.count !== 1 ? 's' : ''} to Archive. You can find them in Vault → Archived.</>
              ) : (
                <>
                  This will permanently delete <b>{confirm.count}</b> scan{confirm.count !== 1 ? 's' : ''} and all associated results.
                  This cannot be undone.
                  <br /><br />
                  Saved searches and active Loop Scans are protected and will not be touched.
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={busy}
                data-testid="history-bulk-confirm-cancel"
                className="zita-btn-secondary text-sm flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => runBulk(confirm.action, confirm.allUnsaved)}
                disabled={busy}
                data-testid="history-bulk-confirm-apply"
                className={`text-sm flex-1 ${
                  confirm.action === 'archive'
                    ? 'zita-btn-primary'
                    : 'zita-btn-primary bg-red-600 hover:bg-red-700'
                }`}
              >
                {busy ? 'Working…' : confirm.action === 'archive' ? 'Archive' : 'Clear permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SAVED SEARCHES tab
// ---------------------------------------------------------------------------

const SavedTab = ({ user }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/scans/history', { params: { kind: 'saved' } });
      setScans(data.scans || []);
    } catch { toast.error('Could not load saved searches.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rename = async (scan) => {
    const name = window.prompt('New name:', scan.saved_name || scan.command_text || 'Saved search');
    if (!name) return;
    try {
      await api.post(`/scans/${scan.id}/rename`, { name });
      toast.success('Renamed.');
      load();
    } catch { toast.error('Could not rename.'); }
  };

  const unsave = async (scan) => {
    if (!window.confirm('Move this back to 90-day history (not permanent anymore)?')) return;
    try {
      await api.post(`/scans/${scan.id}/unsave`);
      toast.success('Moved to history.');
      load();
    } catch { toast.error('Could not unsave.'); }
  };

  const doDelete = async (scan) => {
    if (!window.confirm('Permanently delete this saved search? This cannot be undone.')) return;
    try {
      await api.delete(`/scans/${scan.id}`);
      toast.success('Deleted.');
      load();
    } catch { toast.error('Could not delete.'); }
  };

  const rerun = async (scan) => {
    try {
      const { data } = await api.post(`/scans/${scan.id}/rerun`);
      toast.success('New scan queued.');
      window.location.assign(`/results/${data.scan_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rerun.'); }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-sm zita-ink-muted">Loading…</div>
      ) : scans.length === 0 ? (
        <EmptyState icon={Bookmark} title="Nothing saved yet"
          body="Save a scan from History to keep it forever, or ask Ari: 'save this search'." />
      ) : (
        <div className="space-y-3" data-testid="saved-scan-list">
          {scans.map((s) => (
            <ScanCard
              key={s.id} scan={s} showRenameUnsave
              onRename={rename} onUnsave={unsave}
              onDelete={doDelete} onRerun={rerun}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SCHEDULED SCANS tab
// ---------------------------------------------------------------------------

const ScheduledTab = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/schedules');
      setItems(data.schedules || []);
    } catch { toast.error('Could not load schedules.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const opened = useMemo(() => items.find((s) => s.id === openId), [items, openId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="schedules-new-btn"
          onClick={() => setCreating(true)}
          className="zita-btn-primary text-sm inline-flex items-center gap-1"
        >
          <Plus size={14} /> New schedule
        </button>
        <div className="text-xs zita-ink-muted">
          Scheduled scans run the full Zita Pro engine — every source, every engine, every capability.
          Guided Scan is never automated — Zita never signs into a marketplace on its own.
        </div>
      </div>

      {loading ? (
        <div className="text-sm zita-ink-muted">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarClock} title="No scheduled scans yet"
          body="Create a schedule so Zita keeps searching for you." />
      ) : (
        <div className="space-y-3" data-testid="schedules-list">
          {items.map((s) => (
            <ScheduleCard key={s.id} schedule={s} onOpen={setOpenId} onChanged={load} />
          ))}
        </div>
      )}

      {creating && (
        <ScheduleFormModal
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
          hasPlus={true}
        />
      )}
      {opened && (
        <ScheduleRunsModal schedule={opened} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ScanCard = ({ scan, retentionDays, showRenameUnsave,
                   onSave, onDelete, onRerun, onRename, onUnsave }) => {
  const daysLeft = scan.days_until_purge;
  const warning = daysLeft !== undefined && daysLeft <= 7;
  return (
    <div className="zita-card p-4 space-y-2" data-testid={`scan-card-${scan.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {scan.is_saved && <Bookmark size={14} className="zita-blue-text shrink-0" />}
            {scan.schedule_id && <CalendarClock size={14} className="ari-accent shrink-0" />}
            <Link to={`/results/${scan.id}`}
              className="font-serif text-lg text-zita-espresso truncate hover:underline">
              {scan.saved_name || scan.command_text || '(extension push)'}
            </Link>
          </div>
          <div className="text-xs zita-ink-muted mt-0.5">
            Started {timeAgo(scan.created_at)}
            {' · '}
            <span className="capitalize">{(scan.status || '').replace(/_/g, ' ')}</span>
            {' · '}
            {scan.total_listings} listing{scan.total_listings !== 1 ? 's' : ''}
            {scan.schedule_run_number ? ` · Run #${scan.schedule_run_number}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!scan.is_saved && onSave && (
            <button type="button" onClick={() => onSave(scan)}
              data-testid={`scan-save-${scan.id}`}
              className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-blue-text"
              aria-label="Save permanently">
              <BookmarkPlus size={16} />
            </button>
          )}
          {showRenameUnsave && (
            <>
              <button type="button" onClick={() => onRename?.(scan)}
                data-testid={`scan-rename-${scan.id}`}
                className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-soft"
                aria-label="Rename">
                <Edit3 size={14} />
              </button>
              <button type="button" onClick={() => onUnsave?.(scan)}
                data-testid={`scan-unsave-${scan.id}`}
                className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-muted"
                aria-label="Unsave">
                <Bookmark size={14} />
              </button>
            </>
          )}
          {onRerun && (
            <button type="button" onClick={() => onRerun(scan)}
              data-testid={`scan-rerun-${scan.id}`}
              className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-soft"
              aria-label="Run again">
              <Play size={14} />
            </button>
          )}
          {onDelete && (
            <button type="button" onClick={() => onDelete(scan)}
              data-testid={`scan-delete-${scan.id}`}
              className="p-2 rounded-full hover:bg-red-50 text-red-600"
              aria-label="Delete">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {(scan.delta_new || scan.delta_price_changed || scan.delta_changed
        || scan.delta_reappeared || scan.delta_removed) ? (
        <div className="flex flex-wrap gap-1 text-[11px]" data-testid={`scan-deltas-${scan.id}`}>
          {scan.delta_new > 0 && <Chip label={`${scan.delta_new} New`} tone="blue" />}
          {scan.delta_price_changed > 0 && <Chip label={`${scan.delta_price_changed} Price change`} tone="amber" />}
          {scan.delta_changed > 0 && <Chip label={`${scan.delta_changed} Updated`} tone="purple" />}
          {scan.delta_reappeared > 0 && <Chip label={`${scan.delta_reappeared} Reappeared`} tone="green" />}
          {scan.delta_removed > 0 && <Chip label={`${scan.delta_removed} Removed`} tone="gray" />}
        </div>
      ) : null}

      {!scan.is_saved && !scan.schedule_id && daysLeft !== undefined && (
        <div className={`text-[11px] flex items-center gap-1 ${warning ? 'text-red-600' : 'zita-ink-muted'}`}>
          {warning && <AlertTriangle size={11} />}
          Auto-deletes in {daysLeft} day{daysLeft !== 1 ? 's' : ''}. Save it to keep it forever.
        </div>
      )}
    </div>
  );
};

const ScheduleCard = ({ schedule, onOpen, onChanged }) => {
  const pausedDown = schedule.status === 'paused_awaiting_downgrade';
  const pause = async () => {
    try { await api.post(`/schedules/${schedule.id}/pause`); toast.success('Paused.'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not pause.'); }
  };
  const resume = async () => {
    try { await api.post(`/schedules/${schedule.id}/resume`); toast.success('Resumed.'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not resume.'); }
  };
  const runNow = async () => {
    try { await api.post(`/schedules/${schedule.id}/run-now`); toast.success('Started.'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not run.'); }
  };
  const doDelete = async () => {
    if (!window.confirm(`Delete schedule "${schedule.name}"? Past runs stay in history as one-offs.`)) return;
    try { await api.delete(`/schedules/${schedule.id}`); toast.success('Deleted.'); onChanged(); }
    catch (e) { toast.error('Could not delete.'); }
  };
  const downgrade = async () => {
    if (!window.confirm('Change this schedule to Standard Smart Scan (no Ari Search+ analysis)?')) return;
    try {
      await api.post(`/schedules/${schedule.id}/downgrade`, { to: 'standard' });
      toast.success('Downgraded to Standard.'); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not downgrade.'); }
  };
  const cadenceLabel = (() => {
    const rt = (schedule.run_times || []).map((t) => `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`).join(', ');
    if (schedule.cadence === 'weekly') {
      return `Weekly · ${(schedule.weekday || '').toUpperCase()} at ${rt}`;
    }
    if (schedule.cadence === 'custom') return `Custom · ${schedule.cron_expression}`;
    return `${schedule.cadence.replace(/_/g, ' ')} · ${rt}`;
  })();

  return (
    <div className="zita-card p-4 space-y-2" data-testid={`schedule-card-${schedule.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <CalendarClock size={14} className="ari-accent shrink-0" />
            <div className="font-serif text-lg text-zita-espresso truncate">{schedule.name}</div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
              schedule.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                : schedule.status === 'paused' ? 'bg-orange-50 text-orange-700'
                : schedule.status === 'paused_awaiting_downgrade' ? 'bg-amber-100 text-amber-800'
                : 'bg-red-50 text-red-700'
            }`}>{schedule.status.replace(/_/g, ' ')}</span>
          </div>
          <div className="text-xs zita-ink-muted mt-1">
            {cadenceLabel}
            {' · '}
            Runs: {schedule.run_count || 0}
            {schedule.next_run_at && !pausedDown && (
              <> {' · '} Next: {new Date(schedule.next_run_at).toLocaleString()}</>
            )}
            {schedule.last_run_at && <> {' · '} Last: {timeAgo(schedule.last_run_at)}</>}
          </div>
          <div className="text-[11px] zita-ink-muted italic truncate mt-0.5">
            Query: &ldquo;{schedule.command_text}&rdquo;
          </div>
          {(schedule.recent_errors || []).slice(-1).map((e, i) => (
            <div key={i} className="mt-1 text-[11px] text-red-600 flex items-center gap-1">
              <AlertTriangle size={11} /> {e.message}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1 shrink-0">
          {schedule.status === 'active' && (
            <button type="button" onClick={pause} data-testid={`schedule-pause-${schedule.id}`}
              className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-soft" aria-label="Pause">
              <Pause size={14} />
            </button>
          )}
          {schedule.status === 'paused' && (
            <button type="button" onClick={resume} data-testid={`schedule-resume-${schedule.id}`}
              className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-blue-text" aria-label="Resume">
              <PlayCircle size={14} />
            </button>
          )}
          <button type="button" onClick={runNow} data-testid={`schedule-run-now-${schedule.id}`}
            className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-blue-text" aria-label="Run now">
            <RefreshCw size={14} />
          </button>
          <button type="button" onClick={() => onOpen(schedule.id)} data-testid={`schedule-open-${schedule.id}`}
            className="p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-soft" aria-label="View runs">
            <Clock size={14} />
          </button>
          <button type="button" onClick={doDelete} data-testid={`schedule-delete-${schedule.id}`}
            className="p-2 rounded-full hover:bg-red-50 text-red-600" aria-label="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {pausedDown && (
        <div className="mt-2 border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-sm text-emerald-900 space-y-2" data-testid={`schedule-downgrade-prompt-${schedule.id}`}>
          <div className="font-semibold flex items-center gap-1"><Sparkles size={13} /> Zita Pro now includes what this schedule needs</div>
          <div>
            Ari Search+ has been consolidated into Zita Pro. Resume this
            schedule and it will run at the full Zita Pro capability — every
            source, every engine, no add-on required.
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={resume} data-testid={`schedule-resume-consolidated-${schedule.id}`} className="zita-btn-primary text-xs">
              Resume schedule
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ScheduleRunsModal = ({ schedule, onClose, onChanged }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/schedules/${schedule.id}/runs`);
      setRuns(data.runs || []);
    } catch { toast.error('Could not load runs.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [schedule.id]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" data-testid={`schedule-runs-modal-${schedule.id}`}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/45 backdrop-blur-sm" />
      <div className="relative w-full max-w-3xl zita-card p-5 sm:p-6 max-h-[90vh] overflow-y-auto zita-scroll">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="font-serif text-2xl zita-ink flex items-center gap-2">
            <CalendarClock size={18} className="ari-accent" /> {schedule.name}
          </h2>
          <button type="button" onClick={onClose} data-testid="schedule-runs-close" className="text-sm zita-ink-muted hover:zita-ink">Close</button>
        </div>
        {openRun ? (
          <RunDeltaDetail schedule={schedule} run={openRun} onBack={() => setOpenRun(null)} />
        ) : loading ? (
          <div className="text-sm zita-ink-muted">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="text-sm zita-ink-muted">No runs yet.</div>
        ) : (
          <div className="space-y-2" data-testid="schedule-runs-list">
            {runs.map((r) => (
              <button
                key={r.id} type="button"
                data-testid={`schedule-run-${r.id}`}
                onClick={() => setOpenRun(r)}
                className="w-full text-left zita-card p-3 hover:shadow-md transition"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm zita-ink font-medium">
                    Run #{r.schedule_run_number} · {timeAgo(r.created_at)}
                  </div>
                  <div className="text-[11px] zita-ink-muted capitalize">
                    {(r.status || '').replace(/_/g, ' ')} · {r.total_listings} listings
                  </div>
                </div>
                <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                  {r.delta_new > 0 && <Chip label={`${r.delta_new} New`} tone="blue" />}
                  {r.delta_price_changed > 0 && <Chip label={`${r.delta_price_changed} Price change`} tone="amber" />}
                  {r.delta_changed > 0 && <Chip label={`${r.delta_changed} Updated`} tone="purple" />}
                  {r.delta_reappeared > 0 && <Chip label={`${r.delta_reappeared} Reappeared`} tone="green" />}
                  {r.delta_removed > 0 && <Chip label={`${r.delta_removed} Removed`} tone="gray" />}
                  {r.delta_previously_seen > 0 && <Chip label={`${r.delta_previously_seen} Same`} tone="silver" />}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RunDeltaDetail = ({ schedule, run, onBack }) => {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/schedules/${schedule.id}/runs/${run.id}/deltas`).then(({ data }) => setDetail(data)).catch(() => setDetail(null));
  }, [schedule.id, run.id]);
  const buckets = detail?.buckets || {};
  const bucketOrder = [
    ['new', 'New', 'blue'], ['price_changed', 'Price Changes', 'amber'],
    ['changed', 'Updated', 'purple'], ['reappeared', 'Reappeared', 'green'],
    ['removed', 'Removed', 'gray'], ['previously_seen', 'Previously Seen', 'silver'],
  ];
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="text-xs inline-flex items-center gap-1 zita-blue-text hover:underline" data-testid="run-delta-back">
        <ArrowLeft size={12} /> All runs
      </button>
      <div className="text-sm zita-ink">Run #{run.schedule_run_number} · {new Date(run.created_at).toLocaleString()}</div>
      {!detail ? (
        <div className="text-xs zita-ink-muted">Loading changes…</div>
      ) : (
        bucketOrder.map(([key, label, tone]) => (
          (buckets[key] || []).length === 0 ? null : (
            <div key={key} className="space-y-1">
              <div className="text-xs uppercase tracking-widest zita-ink-muted flex items-center gap-2">
                {label} <Chip label={buckets[key].length} tone={tone} />
              </div>
              {buckets[key].slice(0, 20).map((l) => (
                <div key={l.id} className="border border-[color:var(--zita-hairline)] rounded-lg p-2 text-sm flex justify-between gap-2" data-testid={`delta-listing-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate zita-ink font-medium">{l.title}</div>
                    <div className="text-[11px] zita-ink-muted">
                      {l.source_name}
                      {l.price != null && (<> · ${l.price}</>)}
                      {key === 'price_changed' && l.delta_previous_price != null && (
                        <> (was ${l.delta_previous_price})</>
                      )}
                      {key === 'removed' && <> · no longer available</>}
                    </div>
                  </div>
                  {l.external_url && (
                    <a href={l.external_url} target="_blank" rel="noopener noreferrer" className="text-xs zita-blue-text hover:underline shrink-0">Open</a>
                  )}
                </div>
              ))}
              {(buckets[key] || []).length > 20 && (
                <div className="text-[11px] zita-ink-muted italic">…and {buckets[key].length - 20} more.</div>
              )}
            </div>
          )
        ))
      )}
    </div>
  );
};

const ScheduleFormModal = ({ onClose, onSaved, hasPlus }) => {
  const [name, setName] = useState('');
  const [commandText, setCommandText] = useState('');
  const [cadence, setCadence] = useState('daily');
  const [runTimes, setRunTimes] = useState([{ hour: 8, minute: 0 }]);
  const [weekday, setWeekday] = useState('mon');
  const [mode, setMode] = useState('standard');
  const [saving, setSaving] = useState(false);

  const requiredCount = CADENCE_OPTIONS.find((o) => o.value === cadence)?.runTimes || 1;
  useEffect(() => {
    setRunTimes((prev) => {
      const next = [...prev];
      while (next.length < requiredCount) next.push({ hour: (next[next.length - 1]?.hour || 8) + 4, minute: 0 });
      return next.slice(0, requiredCount);
    });
  }, [cadence, requiredCount]);

  const submit = async () => {
    if (!name.trim() || !commandText.trim()) {
      toast.error('Name and search phrase are required.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/schedules', {
        name: name.trim(),
        command_text: commandText.trim(),
        mode,
        cadence,
        run_times: runTimes,
        weekday: cadence === 'weekly' ? weekday : null,
        timezone_name: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      });
      toast.success('Schedule created.');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" data-testid="schedule-form-modal">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/45 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg zita-card p-6 space-y-3 max-h-[90vh] overflow-y-auto zita-scroll">
        <h2 className="font-serif text-2xl zita-ink flex items-center gap-2">
          <CalendarClock size={16} className="ari-accent" /> New scheduled scan
        </h2>
        <Field label="Schedule name">
          <input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="schedule-form-name" placeholder="e.g. Refrigerators near Akron"
            className="zita-input w-full px-3 py-2 text-sm" />
        </Field>
        <Field label="Search phrase (natural language)">
          <input value={commandText} onChange={(e) => setCommandText(e.target.value)}
            data-testid="schedule-form-command" placeholder="e.g. refrigerator under $200 in Akron OH"
            className="zita-input w-full px-3 py-2 text-sm" />
        </Field>
        <Field label="How often">
          <select value={cadence} onChange={(e) => setCadence(e.target.value)}
            data-testid="schedule-form-cadence" className="zita-input w-full px-3 py-2 text-sm">
            {CADENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        {cadence === 'weekly' && (
          <Field label="Day of week">
            <select value={weekday} onChange={(e) => setWeekday(e.target.value)}
              data-testid="schedule-form-weekday" className="zita-input w-full px-3 py-2 text-sm">
              {WEEKDAYS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </Field>
        )}
        <Field label={`Run time${requiredCount > 1 ? 's' : ''} (${requiredCount} required)`}>
          <div className="space-y-2">
            {runTimes.map((rt, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`schedule-form-time-row-${idx}`}>
                <input type="number" min={0} max={23} value={rt.hour}
                  onChange={(e) => setRunTimes((prev) => prev.map((v, i) => i === idx ? { ...v, hour: Math.max(0, Math.min(23, +e.target.value || 0)) } : v))}
                  data-testid={`schedule-form-hour-${idx}`}
                  className="zita-input w-20 px-2 py-1 text-sm" />
                <span>:</span>
                <input type="number" min={0} max={59} value={rt.minute}
                  onChange={(e) => setRunTimes((prev) => prev.map((v, i) => i === idx ? { ...v, minute: Math.max(0, Math.min(59, +e.target.value || 0)) } : v))}
                  data-testid={`schedule-form-minute-${idx}`}
                  className="zita-input w-20 px-2 py-1 text-sm" />
                <span className="text-xs zita-ink-muted">(24h local time)</span>
              </div>
            ))}
          </div>
        </Field>
        <div className="text-xs zita-ink-muted mt-2 mb-1">
          Every schedule runs with the full Zita Pro engine — deep search,
          all engines, complete source coverage.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="text-sm zita-ink-muted">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="schedule-form-submit"
            className="zita-btn-primary text-sm">
            {saving ? 'Saving…' : 'Create schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest zita-ink-muted">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Chip = ({ label, tone = 'blue' }) => {
  const map = {
    blue: 'bg-[hsl(var(--zita-blue-soft))] zita-blue-text',
    amber: 'bg-orange-50 text-orange-700',
    green: 'bg-emerald-50 text-emerald-700',
    purple: 'bg-[hsl(var(--ari-purple-soft))] ari-accent',
    gray: 'bg-[hsl(var(--zita-silver-soft))] zita-ink-muted line-through',
    silver: 'bg-[hsl(var(--zita-silver-soft))] zita-ink-muted',
  };
  return <span className={`px-2 py-0.5 rounded-full ${map[tone]}`}>{label}</span>;
};

export default Scans;
